import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useErrorText } from '../../shared/api/useErrorText';
import { useForm } from 'react-hook-form';
import { useAuth } from '../../shared/auth/AuthContext';
import { TextField, Button } from '../../shared/ui';
import { LanguageSwitcher } from '../../shared/LanguageSwitcher';
import { HOME } from '../../shared/layout/navigation';

interface LoginForm {
  email: string;
  password: string;
}

export function LoginPage() {
  const { t } = useTranslation();
  const errorText = useErrorText();
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const successMessage = (location.state as { message?: string } | null)?.message;
  const [rememberMe, setRememberMe] = useState(
    () => localStorage.getItem('myscaw_remember_me') === 'true',
  );
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const { register, handleSubmit } = useForm<LoginForm>({
    defaultValues: {
      email: localStorage.getItem('myscaw_saved_email') || '',
      password: '',
    },
  });

  const onSubmit = async (data: LoginForm) => {
    setError(null);
    setSubmitting(true);
    try {
      if (rememberMe) {
        localStorage.setItem('myscaw_remember_me', 'true');
        localStorage.setItem('myscaw_saved_email', data.email);
      } else {
        localStorage.removeItem('myscaw_remember_me');
        localStorage.removeItem('myscaw_saved_email');
      }
      await signIn(data.email, data.password);
      navigate(HOME, { replace: true });
    } catch (e: any) {
      if (e?.code === 'RATE_LIMITED') {
        setError(t('errors.rateLimited'));
      } else {
        setError(errorText(e, 'auth.signInFailed'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-surface font-body-md text-on-surface flex items-center justify-center min-h-screen">
      <main className="w-full max-w-md px-6 py-12">
        <div className="w-full max-w-md bg-surface-container-lowest rounded-3xl border border-hairline p-6 sm:p-10 shadow-soft relative overflow-hidden mx-auto">
          <div className="absolute -top-20 -right-20 w-48 h-48 bg-tertiary-fixed opacity-70 rounded-full blur-3xl pointer-events-none"></div>
          <div className="absolute -bottom-20 -left-20 w-48 h-48 bg-module-restaurant opacity-70 rounded-full blur-3xl pointer-events-none"></div>
          <div className="absolute top-4 right-4 z-20">
            <LanguageSwitcher />
          </div>
          <div className="relative z-10">
            <div className="text-center mb-10">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-xl bg-surface-container-high mb-6 shadow-sm">
                <span className="material-symbols-outlined filled text-4xl text-primary">
                  school
                </span>
              </div>
              <h1 className="font-headline-xl text-headline-xl text-primary mb-2">
                {t('auth.welcomeBack')}
              </h1>
              <p className="font-body-md text-body-md text-on-surface-variant">
                {t('auth.signInContinue')}
              </p>
            </div>

            <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
              <div className="space-y-4">
                <TextField
                  icon="person"
                  placeholder={t('auth.studentIdEmail')}
                  type="text"
                  {...register('email', { required: true })}
                />
                <TextField
                  icon="lock"
                  placeholder={t('auth.password')}
                  type={showPassword ? 'text' : 'password'}
                  {...register('password', { required: true })}
                  trailing={
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="p-1 text-on-surface-variant hover:text-primary rounded-full hover:bg-surface-container-high transition-colors focus:outline-none"
                      title={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
                      aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
                    >
                      <span className="material-symbols-outlined text-[20px] block">
                        {showPassword ? 'visibility' : 'visibility_off'}
                      </span>
                    </button>
                  }
                />
              </div>

              {successMessage && (
                <p className="text-primary text-body-md text-center">{successMessage}</p>
              )}
              {error && <p className="text-error text-body-md text-center">{error}</p>}

              <div className="flex flex-wrap items-center justify-between gap-3">
                <label className="flex items-center space-x-2.5 cursor-pointer group select-none">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="sr-only"
                  />
                  <span
                    className={`w-5 h-5 rounded-md border flex items-center justify-center transition-all duration-150 ${
                      rememberMe
                        ? 'bg-primary border-primary text-on-primary shadow-xs'
                        : 'border-outline-variant bg-surface-container-lowest group-hover:border-primary/60'
                    }`}
                  >
                    {rememberMe && (
                      <span className="material-symbols-outlined text-[15px] font-bold text-white">
                        check
                      </span>
                    )}
                  </span>
                  <span className="font-body-md text-body-md text-on-surface-variant group-hover:text-on-surface transition-colors">
                    {t('auth.rememberMe')}
                  </span>
                </label>
                <Link
                  to="/forgot-password"
                  className="font-label-sm text-label-sm text-primary hover:text-primary-container cursor-pointer"
                >
                  {t('auth.forgotPassword')}
                </Link>
              </div>

              <Button type="submit" className="w-full" disabled={submitting}>
                {t('auth.signIn')}
                <span className="material-symbols-outlined text-[20px]">arrow_forward</span>
              </Button>
            </form>

            <div className="mt-8 text-center">
              <p className="font-body-md text-body-md text-on-surface-variant">
                {t('auth.newHere')}{' '}
                <Link
                  to="/signup"
                  className="font-title-md text-body-md text-primary hover:underline underline-offset-4 ml-1"
                >
                  {t('auth.createAccount')}
                </Link>
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
